import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ServiceContactForm from "@/components/ServiceContactForm";
import ExpressEstimateForm from "@/components/ExpressEstimateForm";

const FALLBACK_ICON = "Gem";

/**
 * Ordre des services : les 2 offres "vendre" en tête, puis la recherche personnalisée.
 */
const FLAGSHIP_SERVICES = [
  {
    id: "cash",
    slug: "buyout",
    kicker: "Réponse en 24-48 h",
    icon: "Wallet",
    title: "Achat cash",
    tagline: "Nous rachetons votre véhicule de prestige. Paiement immédiat.",
    body: [
      "Vous souhaitez vendre rapidement, sans négociation interminable, sans annonces publiques ni visites d'inconnus ? Nous vous faisons une offre ferme sous 24 à 48 heures, basée sur les cotes Argus / Autoscout24 / Mobile.de et sur l'état réel de votre auto.",
      "Aucune surprise : nous nous déplaçons pour l'expertise, nous prenons en charge tous les frais administratifs (rose, Car-Pass, radiation) et le paiement est effectué par virement le jour de la remise des clés.",
    ],
    bullets: [
      "Offre écrite ferme sous 24-48 h",
      "Expertise gratuite sur place ou en showroom",
      "Prise en charge complète des formalités",
      "Virement bancaire le jour de la transaction",
    ],
    formTitle: "Demandez une offre de rachat",
    subject: "Achat cash — proposition de rachat",
    placeholder: "État général, entretien récent, options, raison de la vente…",
    extraFields: [
      { name: "vehicle_ref", label: "Véhicule (Marque · Modèle · Année)", placeholder: "Porsche 991.2 Carrera 4S · 2018" },
      { name: "km", label: "Kilométrage", placeholder: "48 000" },
    ],
  },
  {
    id: "depot",
    slug: "consignment",
    kicker: "Vendez au juste prix",
    icon: "Handshake",
    title: "Dépôt-vente",
    tagline: "Confiez-nous votre auto — nous obtenons le meilleur prix pour vous.",
    body: [
      "Notre formule dépôt-vente est pensée pour les propriétaires qui veulent maximiser la valeur de revente sans les tracas : reportage photo/vidéo professionnel réalisé dans notre showroom, annonces sur les plus grands portails européens, présentation aux acheteurs pré-qualifiés de notre carnet d'adresses.",
      "Aucun frais d'entrée. Une commission unique sur le prix de vente final. Votre voiture reste assurée par nos soins pendant toute la durée du mandat.",
    ],
    bullets: [
      "Reportage photo/vidéo pro dans notre showroom",
      "Diffusion sur Autoscout24, Mobile.de, La Centrale, réseaux privés",
      "Filtrage rigoureux des acheteurs — pas de curieux",
      "Commission unique · pas de frais cachés",
    ],
    formTitle: "Confiez-nous votre véhicule",
    subject: "Dépôt-vente — demande de mise en mandat",
    placeholder: "Modèle, année, kilométrage, entretien, prix souhaité, disponibilité pour dépôt…",
    extraFields: [
      { name: "vehicle_ref", label: "Véhicule (Marque · Modèle · Année)", placeholder: "Porsche 992 Carrera S · 2022" },
      { name: "target_price", label: "Prix de vente souhaité (€)", placeholder: "125 000" },
    ],
  },
  {
    id: "recherche",
    slug: "search",
    kicker: "Service sur-mesure",
    icon: "Search",
    title: "Recherche personnalisée",
    tagline: "Nous trouvons la voiture rare que vous rêvez de posséder.",
    body: [
      "Vous cherchez une Porsche 992 GT3 Touring en configuration précise, une 911 Turbo S avec pack Sonderwunsch, une Alpine A110 R en teinte non-catalogue, ou un youngtimer à l'historique irréprochable ? Notre réseau européen d'apporteurs, de collectionneurs et de concessionnaires officiels nous permet de sourcer des exemplaires que vous ne verrez jamais sur le marché ouvert.",
      "Chaque véhicule proposé est inspecté physiquement par nos experts avant que vous n'en entendiez parler. Historique, kilométrage certifié, matching numbers, options d'origine — tout est vérifié.",
    ],
    bullets: [
      "Réseau exclusif Europe · Allemagne · Suisse · Italie",
      "Inspection physique et rapport de 47 points",
      "Négociation menée à votre place — vous ne payez qu'à la livraison",
      "Discrétion garantie sur toute la mission",
    ],
    formTitle: "Décrivez la voiture que vous recherchez",
    subject: "Recherche personnalisée — brief client",
    placeholder: "Marque, modèle, année, kilométrage max, teinte, options prioritaires, budget indicatif…",
    extraFields: [
      { name: "target_model", label: "Modèle recherché", placeholder: "Porsche 992 GT3 Touring, Alpine A110 R…" },
      { name: "budget", label: "Budget indicatif (€)", placeholder: "200 000 – 250 000" },
    ],
  },
];

export default function Services() {
  const { t, pick } = useLang();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/site").then((r) => setServices(r.data.services || [])).catch(() => setServices([]));
  }, []);

  const items = services.length > 0
    ? services.map((s) => ({ icon: s.icon || FALLBACK_ICON, title: pick(s.title), text: pick(s.text) }))
    : (t.services.items || []).map((s, i) => ({ icon: ["Car","DollarSign","Handshake","RefreshCw","CreditCard","Truck","Search","Gem"][i % 8], title: s.title, text: s.text }));

  return (
    <div data-testid="services-page" className="pt-40 pb-32 bg-[#0D0D0D] min-h-screen">
      <SEO title="Nos services · Achat & Vente Porsche · Dépôt-vente d'exception · Belgique" description="Achat cash sous 24-48 h, dépôt-vente professionnel de voitures d'exception, recherche personnalisée de Porsche de prestige. KV International — spécialiste Porsche à Rhode-Saint-Genèse (Belgique)." />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.35em] text-[#C8A75A] mb-6">02 — Services</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] font-light text-white">{t.services.title}</h1>
        <p className="text-white/60 text-xl mt-6 max-w-2xl font-light">{t.services.subtitle}</p>

        {/* Estimation express — module en tête pour capter les vendeurs immédiats */}
        <div className="mt-16">
          <ExpressEstimateForm />
        </div>

        {/* Ancres rapides vers les 3 services phares */}
        <div className="mt-16 flex flex-wrap gap-3">
          {FLAGSHIP_SERVICES.map((f) => (
            <a key={f.id} href={`#${f.id}`} className="text-xs uppercase tracking-[0.25em] text-[#C8A75A]/80 hover:text-[#C8A75A] border border-[#C8A75A]/30 hover:border-[#C8A75A]/70 rounded-full px-5 py-2.5 transition-colors">
              {f.title}
            </a>
          ))}
        </div>

        {/* Grille récap — tous les services (backend) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {items.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Gem;
            return (
              <div key={i} data-testid={`service-${i}`} className="bg-[#1E1E1E] border border-white/10 rounded-md p-10 hover:border-[#C8A75A]/40 transition-colors duration-500 min-h-[280px]">
                <Icon className="w-10 h-10 text-[#C8A75A] mb-8" strokeWidth={1.1} />
                <h3 className="font-display text-2xl text-white mb-4 leading-[1.15]">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>

        {/* 3 sections détaillées — Recherche perso · Achat cash · Dépôt-vente */}
        {FLAGSHIP_SERVICES.map((s, idx) => {
          const Icon = Icons[s.icon] || Icons.Gem;
          const reverse = idx % 2 === 1;
          return (
            <section
              key={s.id}
              id={s.id}
              data-testid={`flagship-${s.slug}`}
              className="mt-24 scroll-mt-32 border-t border-white/5 pt-24 grid lg:grid-cols-2 gap-14 items-start"
            >
              {/* Description */}
              <div className={reverse ? "lg:order-2" : ""}>
                <div className="flex items-center gap-4 mb-6">
                  <span className="w-14 h-14 rounded-full border border-[#C8A75A]/40 bg-[#C8A75A]/10 flex items-center justify-center text-[#C8A75A]">
                    <Icon className="w-6 h-6" strokeWidth={1.2} />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A]">{s.kicker}</p>
                </div>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] font-light text-white">
                  {s.title}
                </h2>
                <p className="text-white/80 text-lg mt-5 max-w-xl">{s.tagline}</p>

                {s.body.map((p, i) => (
                  <p key={i} className="text-white/60 text-sm leading-relaxed mt-5 max-w-xl">{p}</p>
                ))}

                <ul className="mt-8 space-y-3 max-w-xl">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                      <Icons.Check className="w-4 h-4 text-[#C8A75A] mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Formulaire dédié */}
              <div className={reverse ? "lg:order-1" : ""}>
                <ServiceContactForm
                  testid={`form-${s.slug}`}
                  requestType={s.slug}
                  title={s.formTitle}
                  subject={s.subject}
                  placeholder={s.placeholder}
                  extraFields={s.extraFields}
                />
              </div>
            </section>
          );
        })}

        <div className="mt-32 border-t border-white/10 pt-20 text-center">
          <Link to="/contact" data-testid="services-cta" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-12 py-6 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-3">
            {t.home.cta_book} <Icons.ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
